import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type Crumb = { label: ReactNode; to?: string };

/**
 * Omrvinky nad nadpisom (napr. Projekty › Klientsky portál › Web).
 *
 * Na telefóne sa ukáže iba posledný odkaz so šípkou späť — celá cesta by sa do
 * šírky nezmestila a „späť o úroveň“ je presne to, na čo sa tam ťuká. Na
 * rozdiel od starého „Späť“ (navigate(-1)) vedie vždy na rovnaké miesto, aj
 * keď človek prišiel z upozornenia alebo z iného projektu.
 */
export const Breadcrumbs = ({ items, className }: { items: ReadonlyArray<Crumb>; className?: string }) => {
  const back = [...items].reverse().find((item) => item.to);
  return (
    <nav aria-label="Omrvinky" className={clsx('min-w-0', className)}>
      {back?.to ? (
        <Link to={back.to} className="breadcrumb-back sm:hidden">
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
          <span className="truncate">{back.label}</span>
        </Link>
      ) : null}
      <ol className="hidden min-w-0 items-center gap-1 text-[13px] sm:flex">
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <Fragment key={index}>
              <li className={clsx('min-w-0', last ? 'truncate' : 'shrink-0')}>
                {item.to && !last ? (
                  <Link to={item.to} className="breadcrumb-link">
                    {item.label}
                  </Link>
                ) : (
                  <span className="truncate text-slate-500 dark:text-slate-400" aria-current={last ? 'page' : undefined}>
                    {item.label}
                  </span>
                )}
              </li>
              {last ? null : (
                <li aria-hidden="true" className="shrink-0 text-slate-300 dark:text-slate-600">
                  <ChevronRightIcon className="h-3.5 w-3.5" />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

/**
 * Hlavička obrazovky: (omrvinky), veľký nadpis, jeden riadok pod ním, hlavná
 * akcia vpravo a zriedkavé akcie v menu „…“ vedľa nej.
 *
 * Každá stránka si ju skladala sama, takže Schránka mala malý nadpis a
 * Tím počítadlo zarovnané k spodku nadpisu, kým Projekty k vrchu. Akcie sú tu
 * zarovnané na stred prvého riadku nadpisu (nie na stred celého bloku), aby
 * sa pri dvojriadkovej hlavičke tlačidlo neposúvalo dole k podnadpisu.
 *
 * Do `actions` patrí najviac jedno hlavné tlačidlo a za ním `OverflowMenu` so
 * zriedkavými vecami. Menu sa sem neimportuje zámerne: hlavička je v hlavnom
 * balíku (Domov) a menu by ho zbytočne zväčšilo pre každé spustenie.
 */
export const PageHeader = ({
  title,
  subtitle,
  actions,
  breadcrumbs,
  leading,
  titleAddon,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: ReadonlyArray<Crumb>;
  /** Malý prvok pred nadpisom (farebná bodka projektu, ikona). */
  leading?: ReactNode;
  /** Stav hneď za nadpisom (napr. „live“). Mimo <h1>, aby meno nadpisu
   *  pre čítačku ostalo iba názvom. */
  titleAddon?: ReactNode;
  className?: string;
}) => {
  return (
    <header className={clsx('page-header rise', className)}>
      {breadcrumbs && breadcrumbs.length > 0 ? <Breadcrumbs items={breadcrumbs} className="mb-2" /> : null}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          {leading ? <div className="page-header-leading">{leading}</div> : null}
          <div className="min-w-0">
            {titleAddon ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h1 className="large-title break-words">{title}</h1>
                {titleAddon}
              </div>
            ) : (
              <h1 className="large-title break-words">{title}</h1>
            )}
            {subtitle ? <p className="page-subtitle">{subtitle}</p> : null}
          </div>
        </div>
        {actions ? <div className="page-header-actions">{actions}</div> : null}
      </div>
    </header>
  );
};
